/**
 * 用户状态 - Pinia store
 * 存储登录后的 token、用户信息、权限、角色
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ROLE_NAME_TO_KEY } from '@/constants/roles';
import { useKeepAliveStore } from '@/stores/keepAlive';
import { useTagsViewStore } from '@/stores/tagsView';
import { useAccountUnreadStore } from '@/stores/accountUnread';
import type { MenuItem } from '@/api';

const TOKEN_KEY = 'token';
const USER_KEY = 'user';

export interface UserInfo {
  id?: number;
  user_id?: number;
  username?: string;
  role_name?: string;
  account_id?: number;
  agent_id?: number;
  /** 0=离线 1=在线 2=隐身，与后端 is_online 一致 */
  is_online?: 0 | 1 | 2;
  phone?: string;
  /** 腾讯 IM SDKAppID */
  im_app_id?: number;
  /** 腾讯 IM userId */
  im_identifier?: string;
  /** 腾讯 IM UserSig */
  im_user_sign?: string;
  /** 公海消息声音提醒：未分配给自己的会话来新消息时是否响，0=关 1=开，后端默认 1 */
  public_msg_sound?: 0 | 1;
  /** 个人消息声音提醒：分配给自己的会话来新消息时是否响，0=关 1=开，后端默认 1 */
  personal_msg_sound?: 0 | 1;
  /** 系统提醒弹窗自动消失秒数 */
  notify_popup_seconds?: number;
  /** 客户是否可见意向车型实体：1=可见 0=不可见，决定商家信息是否展示「校准车型」 */
  vehicle_model_visible?: 0 | 1;
  /** 是否允许查看客户手机号/微信号明文 */
  can_view_plain_pii?: 0 | 1;
  [key: string]: unknown;
}

export const useUserStore = defineStore('user', () => {
  const token = ref<string>(localStorage.getItem(TOKEN_KEY) || '');
  const user = ref<UserInfo | null>(
    (() => {
      try {
        const u = localStorage.getItem(USER_KEY);
        return u ? (JSON.parse(u) as UserInfo) : null;
      } catch {
        return null;
      }
    })(),
  );
  const permissions = ref<Array<{ key?: string }>>([]);
  const roleKey = ref<string | null>(null);
  const menus = ref<MenuItem[]>([]);

  const isLoggedIn = computed(() => !!token.value);
  const userId = computed(
    () => user.value?.account_id ?? user.value?.agent_id ?? user.value?.username ?? null,
  );

  /** 第一个叶子菜单的 path，用于登录后默认跳转（递归到任意层级） */
  const firstMenuPath = computed<string>(() => {
    const findLeaf = (list: MenuItem[]): string => {
      for (const menu of list) {
        if (menu.type === 2 && menu.path) return menu.path;
        if (menu.children?.length) {
          const sub = findLeaf(menu.children);
          if (sub) return sub;
        }
      }
      return '';
    };
    return findLeaf(menus.value);
  });

  /** 登录成功：设置 token 和 user，持久化到 localStorage */
  const setLogin = (data: { token: string; user?: UserInfo }) => {
    token.value = data.token;
    user.value = data.user ?? null;
    localStorage.setItem(TOKEN_KEY, data.token);
    if (data.user) localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  };

  /** 设置权限及用户信息（来自 queryAuthInfo），传空可清空 */
  const setAuthInfo = (data?: {
    agent_id?: number;
    app_id?: number;
    app_code?: string;
    username?: string;
    nickname?: string;
    role_name?: string;
    role_code?: string;
    is_online?: 0 | 1 | 2;
    phone?: string;
    public_msg_sound?: 0 | 1;
    personal_msg_sound?: 0 | 1;
    notify_popup_seconds?: number;
    vehicle_model_visible?: 0 | 1;
    can_view_plain_pii?: 0 | 1;
    permissions?: string[] | Array<{ key?: string }>;
    menus?: MenuItem[];
  }) => {
    if (!data) {
      roleKey.value = null;
      permissions.value = [];
      menus.value = [];
      return;
    }
    const roleName = (data?.role_name ?? '').trim();
    roleKey.value = ROLE_NAME_TO_KEY[roleName] ?? 'customer_normal';
    permissions.value = Array.isArray(data?.permissions)
      ? data.permissions.map((p) => (typeof p === 'string' ? { key: p } : p))
      : [];
    /** 合并/更新用户信息 */
    const merged: UserInfo = {
      ...user.value,
      agent_id: data.agent_id,
      app_id: data.app_id,
      app_code: data.app_code,
      username: data.username ?? user.value?.username,
      nickname: data.nickname ?? user.value?.nickname,
      role_name: data.role_name ?? user.value?.role_name,
      role_code: data.role_code,
      is_online: data.is_online,
      phone: data.phone ?? user.value?.phone,
      /** 声音提醒开关：后端 NOT NULL DEFAULT 1，取不到时按开启，与库里默认一致 */
      public_msg_sound: data.public_msg_sound ?? user.value?.public_msg_sound ?? 1,
      personal_msg_sound: data.personal_msg_sound ?? user.value?.personal_msg_sound ?? 1,
      notify_popup_seconds: data.notify_popup_seconds ?? user.value?.notify_popup_seconds ?? 8,
      /** 以每次 /auth/info 返回为准，未返回按不可见处理，不沿用本地旧值 */
      vehicle_model_visible: data.vehicle_model_visible,
      can_view_plain_pii: data.can_view_plain_pii ?? 0,
    };
    user.value = merged;
    localStorage.setItem(USER_KEY, JSON.stringify(merged));
    menus.value = data.menus ?? [];
  };

  /** 更新 is_online 并持久化 */
  const setUserIsOnline = (val: 0 | 1 | 2) => {
    if (user.value) {
      user.value = { ...user.value, is_online: val };
      localStorage.setItem(USER_KEY, JSON.stringify(user.value));
    }
  };

  /**
   * 更新声音提醒开关并持久化。
   * 坐席人员页改到自己那行时调用：initAuth 对同一个 token 只拉一次 /auth/info，
   * 不同步就要刷新浏览器开关才生效。
   */
  const setSoundNotify = (key: 'public_msg_sound' | 'personal_msg_sound', val: 0 | 1) => {
    if (!user.value) return;
    user.value = { ...user.value, [key]: val };
    localStorage.setItem(USER_KEY, JSON.stringify(user.value));
  };

  /** 更新系统提醒弹窗自动消失时间并持久化 */
  const setNotifyPopupSeconds = (seconds: number) => {
    if (!user.value) return;
    user.value = { ...user.value, notify_popup_seconds: seconds };
    localStorage.setItem(USER_KEY, JSON.stringify(user.value));
  };

  /** 更新 IM 三字段（refresh-im-sign 后调用） */
  const updateImCredentials = (creds: {
    im_app_id: number;
    im_identifier: string;
    im_user_sign: string;
  }) => {
    if (!user.value) return;
    user.value = { ...user.value, ...creds };
    localStorage.setItem(USER_KEY, JSON.stringify(user.value));
  };

  /** 登出：清空状态和 localStorage */
  const logout = () => {
    token.value = '';
    user.value = null;
    permissions.value = [];
    roleKey.value = null;
    menus.value = [];
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    /** 清空页面缓存，避免换账号后仍看到上一个账号的列表数据与筛选条件 */
    useKeepAliveStore().clearCache();
    /** 清空顶部标签栏，否则换账号后仍留着上一个账号的已打开页面 */
    useTagsViewStore().clear();
    /** 停掉未读轮询并清空基线，否则换账号后首轮就会拿旧基线比对、误响提示音 */
    useAccountUnreadStore().reset();
  };

  /** 从 localStorage 恢复（页面刷新后） */
  const hydrate = () => {
    token.value = localStorage.getItem(TOKEN_KEY) || '';
    try {
      const u = localStorage.getItem(USER_KEY);
      user.value = u ? (JSON.parse(u) as UserInfo) : null;
    } catch {
      user.value = null;
    }
  };

  const hasPermission = (key: string) => permissions.value.some((p) => p?.key === key);

  /**
   * 按钮权限 key 集合：菜单节点（type≠3）下挂载的子节点 key。
   * 数据权限挂在 type=3 的权限组（如 cus.data.permissions）下，据此与按钮权限区分。
   */
  const btnPermissionKeys = computed<Set<string>>(() => {
    const keys = new Set<string>();
    const walk = (list: MenuItem[]) => {
      for (const item of list) {
        if (!item.children?.length) continue;
        if (item.type !== 3) {
          for (const child of item.children) {
            if (child.key) keys.add(child.key);
          }
        }
        walk(item.children);
      }
    };
    walk(menus.value);
    return keys;
  });

  /** 是否拥有指定按钮权限（页面按钮绑定 key 后据此决定是否展示） */
  const hasBtnPermission = (key: string): boolean => btnPermissionKeys.value.has(key);

  /** 是否拥有指定数据权限：递归遍历 menus 树查找 type=3 且 key 匹配的节点 */
  const hasDataPermission = (key: string): boolean => {
    const walk = (list: MenuItem[]): boolean => {
      for (const item of list) {
        if (item.type === 3 && item.key === key) return true;
        if (item.children?.length && walk(item.children)) return true;
      }
      return false;
    };
    return walk(menus.value);
  };

  /** 获取当前用户 ID（用于非响应式场景） */
  const getUserId = () =>
    user.value?.account_id ?? user.value?.agent_id ?? user.value?.username ?? null;

  return {
    token,
    user,
    permissions,
    roleKey,
    menus,
    isLoggedIn,
    userId,
    firstMenuPath,
    setLogin,
    setAuthInfo,
    setUserIsOnline,
    setSoundNotify,
    setNotifyPopupSeconds,
    updateImCredentials,
    logout,
    hydrate,
    hasPermission,
    hasBtnPermission,
    hasDataPermission,
    getUserId,
  };
});
